import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';
import { useAit } from '../../../context/AitContext';
import {
  X, Send, Maximize2, Sparkles, Plus, Loader2, Trash2, Play, ChevronDown,
  ArrowUpRight, AlertTriangle, CheckCircle2, ExternalLink, Copy, Check,
  Bookmark, BookmarkCheck, StickyNote
} from 'lucide-react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import toast from 'react-hot-toast';
import SavedPromptsModal from '../../common/SavedPromptsModal';
import SavePromptDialog from '../../common/SavePromptDialog';
import AddToNotesModal from '../../notes/AddToNotesModal';
import RotatingStatusIndicator from '../../common/RotatingStatusIndicator';
import { AIT_STATEMENTS } from '../../common/statusStatements';

// Configure marked renderer for links opening in new tabs
const renderer = new marked.Renderer();
const originalLinkRenderer = renderer.link.bind(renderer);
renderer.link = function (href, title, text) {
  const html = originalLinkRenderer(href, title, text);
  return html.replace(/^<a /, '<a target="_blank" rel="noopener noreferrer" ');
};

function wrapTablesForScroll(html) {
  if (typeof document === 'undefined' || !html.includes('<table')) return html;
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  tmp.querySelectorAll('table').forEach((t) => {
    if (t.parentElement?.dataset?.tableScroll === 'true') return;
    const wrap = document.createElement('div');
    wrap.dataset.tableScroll = 'true';
    wrap.style.cssText = 'overflow-x:auto;max-width:100%;margin:0.5rem 0;-webkit-overflow-scrolling:touch';
    t.parentNode.insertBefore(wrap, t);
    wrap.appendChild(t);
  });
  return tmp.innerHTML;
}

function formatMarkdown(text) {
  if (typeof text !== 'string' || !text) return '';
  try {
    const rawHTML = marked(text, { gfm: true, breaks: true, renderer });
    const clean = DOMPurify.sanitize(rawHTML, {
      ADD_ATTR: ['target', 'rel'],
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a', 'hr', 'del',
        'table', 'thead', 'tbody', 'tr', 'td', 'th', 'span', 'div', 'sup', 'sub'
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class']
    });
    return wrapTablesForScroll(clean);
  } catch {
    return text.replace(/\n/g, '<br>');
  }
}

const SUGGESTED_PROMPTS = [
  {
    label: 'Look up AMI limits',
    icon: '🔍',
    message: 'What are the current HUD income limits and rent limits for this property\'s area? Please search for the most recent data.',
  },
  {
    label: 'Compliance summary',
    icon: '📊',
    message: 'Give me a summary of this building\'s current AIT compliance status, including Average Income, Designation Distribution, and any risks.',
  },
  {
    label: 'Optimize designations',
    icon: '⚡',
    message: 'Analyze the current unit designations and suggest optimization strategies to improve compliance headroom while maintaining flexibility.',
  },
  {
    label: 'Over-income options',
    icon: '🛡️',
    message: 'What are the available correction pathways for any over-income units in this building? Evaluate each option\'s impact on compliance.',
  },
];

function AitChatDrawer({ propertyId, propertyName, buildingId, isOpen, onClose }) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const {
    chats, currentChatId, chatMessages, chatLoading,
    fetchChats, createChat, fetchChatMessages, sendChatMessage, deleteChat, modelScenario,
    setCurrentChatId, setChatMessages
  } = useAit();

  const [input, setInput] = useState('');
  const [showChatList, setShowChatList] = useState(false);
  const [scenarioResults, setScenarioResults] = useState({});
  const [runningScenario, setRunningScenario] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showSaveToNotes, setShowSaveToNotes] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [savingContent, setSavingContent] = useState('');
  const [showPromptsModal, setShowPromptsModal] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const isDark = theme === 'dark';

  // Load chats when drawer opens
  useEffect(() => {
    if (isOpen && propertyId) {
      fetchChats(propertyId);
    }
  }, [isOpen, propertyId, fetchChats]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Focus input when chat loads
  useEffect(() => {
    if (isOpen && currentChatId) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, currentChatId]);

  const handleNewChat = async () => {
    const result = await createChat(propertyId);
    if (result.success) {
      setShowChatList(false);
    }
  };

  const handleSelectChat = async (chatId) => {
    await fetchChatMessages(propertyId, chatId);
    setShowChatList(false);
  };

  const handleDeleteChat = async (e, chatId) => {
    e.stopPropagation();
    const result = await deleteChat(propertyId, chatId);
    if (result.success) {
      toast.success('Chat deleted');
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || chatLoading) return;

    let chatId = currentChatId;

    // Auto-create chat if none selected
    if (!chatId) {
      const result = await createChat(propertyId);
      if (!result.success) {
        toast.error('Failed to create chat');
        return;
      }
      chatId = result.chat.id;
    }

    const message = input.trim();
    setInput('');
    await sendChatMessage(propertyId, chatId, message, buildingId);
  };

  const handleRunScenario = async (scenario) => {
    if (!scenario.building_id || !scenario.changes) return;
    setRunningScenario(scenario);

    const result = await modelScenario(propertyId, scenario.building_id, scenario.changes);
    if (result) {
      setScenarioResults(prev => ({
        ...prev,
        [JSON.stringify(scenario.changes)]: result
      }));
    } else {
      toast.error('Failed to run scenario');
    }
    setRunningScenario(null);
  };

  const handleSuggestedPrompt = async (prompt) => {
    let chatId = currentChatId;
    if (!chatId) {
      const result = await createChat(propertyId);
      if (!result.success) {
        toast.error('Failed to create chat');
        return;
      }
      chatId = result.chat.id;
    }
    await sendChatMessage(propertyId, chatId, prompt.message, buildingId);
  };

  const handleExpand = () => {
    onClose();
    navigate(`/dashboard/ait/${propertyId}/chat`);
  };

  const handleCopy = async (msgId, rawText) => {
    const html = formatMarkdown(rawText);
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const plain = tmp.textContent || tmp.innerText || rawText;
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        })
      ]);
    } catch {
      // Fallback for browsers that don't support ClipboardItem
      await navigator.clipboard.writeText(plain);
    }
    setCopiedId(msgId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Parse scenario blocks from message content
  const parseContent = (content) => {
    if (!content) return { text: '', scenarios: [] };

    const scenarioPattern = /```scenario\s*\n([\s\S]*?)\n```/g;
    const scenarios = [];
    let match;

    while ((match = scenarioPattern.exec(content)) !== null) {
      try {
        const scenario = JSON.parse(match[1].trim());
        if (scenario.changes) scenarios.push(scenario);
      } catch (e) { /* skip malformed */ }
    }

    const text = content.replace(/```scenario\s*\n[\s\S]*?\n```/g, '').trim();
    return { text, scenarios };
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-y-0 right-0 z-40 flex">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20" onClick={onClose} />

      {/* Drawer panel */}
      <div className={`relative ml-auto w-full max-w-md flex flex-col shadow-xl ${
        isDark ? 'bg-gray-900' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b shrink-0 ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles size={18} className="text-blue-500 shrink-0" />
            <div className="min-w-0">
              <h3 className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                AIT Assistant
              </h3>
              <p className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {propertyName}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {/* Chat list toggle */}
            <button
              onClick={() => setShowChatList(!showChatList)}
              className={`p-1.5 rounded-lg text-xs ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              title="Chat history"
            >
              <ChevronDown size={16} />
            </button>
            <button
              onClick={handleNewChat}
              className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              title="New chat"
            >
              <Plus size={16} />
            </button>
            <button
              onClick={handleExpand}
              className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              title="Expand to full page"
            >
              <Maximize2 size={16} />
            </button>
            <button
              onClick={onClose}
              className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Chat list dropdown */}
        {showChatList && (
          <div className={`border-b max-h-48 overflow-y-auto shrink-0 ${
            isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'
          }`}>
            {chats.length === 0 ? (
              <p className={`text-xs text-center py-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                No conversations yet
              </p>
            ) : (
              chats.map(chat => (
                <button
                  key={chat.id}
                  onClick={() => handleSelectChat(chat.id)}
                  className={`w-full flex items-center justify-between px-4 py-2 text-left text-xs ${
                    chat.id === currentChatId
                      ? isDark ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-50 text-blue-600'
                      : isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <span className="truncate">{chat.title}</span>
                  <div className="flex items-center gap-1 shrink-0 ml-2">
                    <span className={`${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                      {chat.message_count || 0} msgs
                    </span>
                    <button
                      onClick={(e) => handleDeleteChat(e, chat.id)}
                      className={`p-0.5 rounded ${isDark ? 'hover:bg-gray-600 text-gray-500' : 'hover:bg-gray-200 text-gray-400'}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </button>
              ))
            )}
          </div>
        )}

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {!currentChatId && chatMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Sparkles size={40} className={`mb-3 ${isDark ? 'text-gray-700' : 'text-gray-300'}`} />
              <h4 className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                AIT Compliance Assistant
              </h4>
              <p className={`text-xs max-w-xs mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                Ask about compliance, designations, income limits, or run what-if scenarios.
              </p>
              <div className="w-full max-w-xs space-y-2">
                {SUGGESTED_PROMPTS.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => handleSuggestedPrompt(prompt)}
                    disabled={chatLoading}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left text-xs transition-colors ${
                      isDark
                        ? 'bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700'
                        : 'bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200'
                    } disabled:opacity-50`}
                  >
                    <span className="text-base shrink-0">{prompt.icon}</span>
                    <span className="font-medium">{prompt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {chatMessages.map((msg) => {
                const { text, scenarios } = msg.is_user
                  ? { text: msg.content, scenarios: [] }
                  : parseContent(msg.content);
                const citations = (!msg.is_user && msg.citations) ? msg.citations : [];

                return (
                  <div key={msg.id} className={`flex ${msg.is_user ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm group relative ${
                      msg.is_user
                        ? 'bg-blue-600 text-white'
                        : isDark ? 'bg-gray-800 text-gray-200' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {/* Rendered message */}
                      {msg.is_user ? (
                        <div className="whitespace-pre-wrap break-words">{text}</div>
                      ) : (
                        <div
                          className={`prose prose-sm max-w-none break-words ${
                            isDark ? 'prose-invert' : ''
                          } prose-headings:mt-2 prose-headings:mb-1 prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-hr:my-2 prose-pre:my-1.5 prose-blockquote:my-1.5`}
                          dangerouslySetInnerHTML={{ __html: formatMarkdown(text) }}
                        />
                      )}

                      {/* Save prompt button for user messages */}
                      {msg.is_user && (
                        <div className="flex justify-end mt-1 -mb-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => { setSavingContent(msg.content); setShowSaveDialog(true); }}
                            className="p-0.5 rounded text-blue-200 hover:text-white"
                            title="Save as prompt"
                          >
                            <Bookmark size={11} />
                          </button>
                        </div>
                      )}

                      {/* Action buttons for AI messages */}
                      {!msg.is_user && (
                        <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => { setNoteContent(msg.content); setShowSaveToNotes(true); }}
                            className={`p-0.5 rounded ${isDark ? 'hover:bg-gray-700 text-gray-500' : 'hover:bg-gray-200 text-gray-400'}`}
                            title="Save to Notes"
                          >
                            <StickyNote size={12} />
                          </button>
                          <button
                            onClick={() => handleCopy(msg.id, msg.content)}
                            className={`p-0.5 rounded ${isDark ? 'hover:bg-gray-700 text-gray-500' : 'hover:bg-gray-200 text-gray-400'}`}
                            title="Copy message"
                          >
                            {copiedId === msg.id ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                          </button>
                        </div>
                      )}

                      {/* Citations */}
                      {citations.length > 0 && (
                        <div className={`mt-2 pt-2 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                          <p className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Sources</p>
                          <div className="space-y-0.5">
                            {citations.map((cite, idx) => (
                              <a
                                key={idx}
                                href={cite.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center gap-1 text-xs hover:underline ${
                                  isDark ? 'text-blue-400' : 'text-blue-600'
                                }`}
                              >
                                <ExternalLink size={10} className="shrink-0" />
                                <span className="truncate">{cite.title || cite.url}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Scenario cards */}
                      {scenarios.map((scenario, idx) => {
                        const key = JSON.stringify(scenario.changes);
                        const result = scenarioResults[key];
                        const isRunning = runningScenario && JSON.stringify(runningScenario.changes) === key;

                        return (
                          <div
                            key={idx}
                            className={`mt-2 p-2 rounded-lg border ${
                              isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-white'
                            }`}
                          >
                            <p className={`text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              {scenario.description}
                            </p>

                            {result ? (
                              <div className="space-y-1">
                                <div className="flex items-center gap-2 text-xs">
                                  <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Before:</span>
                                  <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                                    {result.before?.average_income ?? '—'}% avg
                                  </span>
                                  <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>After:</span>
                                  <span className={`font-medium ${
                                    result.after?.compliant ? 'text-green-500' : 'text-red-500'
                                  }`}>
                                    {result.after?.average_income ?? '—'}% avg
                                  </span>
                                </div>
                                {result.delta != null && (
                                  <div className="flex items-center gap-1 text-xs">
                                    {result.after?.compliant ? (
                                      <CheckCircle2 size={12} className="text-green-500" />
                                    ) : (
                                      <AlertTriangle size={12} className="text-red-500" />
                                    )}
                                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                                      Delta: {result.delta > 0 ? '+' : ''}{result.delta}% | Headroom: {result.after?.headroom}
                                    </span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <button
                                onClick={() => handleRunScenario(scenario)}
                                disabled={isRunning}
                                className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                              >
                                {isRunning ? (
                                  <Loader2 size={12} className="animate-spin" />
                                ) : (
                                  <Play size={12} />
                                )}
                                Run Scenario
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {chatLoading && <RotatingStatusIndicator statements={AIT_STATEMENTS} compact />}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input area */}
        {(currentChatId || chatMessages.length > 0) && (
          <div className={`px-4 py-3 border-t shrink-0 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            {/* Quick-action chips when chat is fresh */}
            {chatMessages.length <= 2 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {SUGGESTED_PROMPTS.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => handleSuggestedPrompt(prompt)}
                    disabled={chatLoading}
                    className={`flex items-center gap-1 px-2 py-1 rounded-full text-[11px] transition-colors ${
                      isDark
                        ? 'bg-gray-800 hover:bg-gray-700 text-gray-400 border border-gray-700'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-600 border border-gray-200'
                    } disabled:opacity-50`}
                  >
                    <span>{prompt.icon}</span>
                    {prompt.label}
                  </button>
                ))}
              </div>
            )}
            <form onSubmit={handleSend} className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about compliance..."
                disabled={chatLoading}
                className={`flex-1 px-3 py-2 rounded-lg border text-sm ${
                  isDark
                    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                    : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                } disabled:opacity-50`}
              />
              <button
                type="button"
                onClick={() => setShowPromptsModal(true)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                }`}
                title="Saved prompts"
              >
                <BookmarkCheck size={16} />
              </button>
              <button
                type="submit"
                disabled={!input.trim() || chatLoading}
                className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:hover:bg-blue-600"
              >
                <Send size={16} />
              </button>
            </form>
          </div>
        )}
      </div>
      <SavedPromptsModal
        isOpen={showPromptsModal}
        onClose={() => setShowPromptsModal(false)}
        onUse={(content) => setInput(content)}
        currentFeature="ait"
      />
      <SavePromptDialog
        isOpen={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        content={savingContent}
        featureTag="ait"
      />
      {showSaveToNotes && (
        <AddToNotesModal
          content={noteContent}
          sourceType="ait_chat"
          onClose={() => setShowSaveToNotes(false)}
          theme={isDark ? 'dark' : 'light'}
        />
      )}
    </div>
  );
}

export default AitChatDrawer;
